import constants from '../../../../../appConstants';
import { MultiSelectFilter, StaticFilter } from '@dataGrid/components/columnFilters';
import { mergeUserConfig } from '@dataGrid/gridUtilities';
import { getSpecialNeeds, SpecialNeedsRenderer } from '@dataGrid/components/cellRenderer';

export interface GridConfigConfiguration {
    userProfile?: {
        columnPref?: Record<string, unknown>;
    };
}

export interface TrailerStopColumnDef {
    headerName: string;
    headerTooltip: string;
    field?: string;
    minWidth: number;
    cellClass: string;
    filter?: typeof MultiSelectFilter | typeof StaticFilter;
    filterParams?: Record<string, unknown>;
    tooltipValueGetter?: (params: { value: unknown }) => unknown;
    valueGetter?: (params: { data: Record<string, unknown> }) => unknown;
    cellRenderer?: unknown;
}

export interface TrailerStopGridConfig {
    title: string;
    keyColumn: string;
    rowSelection: string;
    rowDeselection: boolean;
    defaultColDef: {
        sortable: boolean;
        filter: boolean;
        resizable: boolean;
        editable: boolean;
    };
    columns: TrailerStopColumnDef[];
    toggles?: Record<string, unknown>;
}

export default class GridConfig {
    name: string;
    config: TrailerStopGridConfig;

    constructor(configuration?: GridConfigConfiguration) {
        const config = configuration || {};
        this.name = 'GridConfiguration';
        this.config = GridConfig.getConfiguration(config) as TrailerStopGridConfig;
    }

    static getConfiguration({ userProfile }: GridConfigConfiguration = {}): TrailerStopGridConfig {
        const defaultConfig = GridConfig.getDefaultConfiguration(userProfile);
        const userConfig = userProfile?.columnPref;
        return mergeUserConfig({
            defaultConfig,
            userConfig,
        }) as TrailerStopGridConfig;
    }

    static getSpecialNeedsOption(params: { context?: { specialNeedsOptions?: unknown } }): unknown {
        return params.context?.specialNeedsOptions;
    }

    static getDefaultConfiguration(userProfile?: GridConfigConfiguration['userProfile']): TrailerStopGridConfig {
        return {
            title: constants.gridNames.ClosedTrailersGrid,
            keyColumn: 'plannedStopSequence',
            rowSelection: 'single',
            rowDeselection: true,
            defaultColDef: {
                sortable: true,
                filter: true,
                resizable: true,
                editable: false,
            },
            columns: [
                {
                    headerName: 'Stop Seq',
                    headerTooltip: 'Stop Sequence',
                    field: 'plannedStopSequence',
                    minWidth: constants.columnWidth.W_120,
                    cellClass: 'gridCell-leftAlign',
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'Pro#',
                    headerTooltip: 'Pro Number',
                    field: 'proNumbers',
                    minWidth: constants.columnWidth.W_160,
                    cellClass: 'gridCell-leftAlign',
                    tooltipValueGetter: (params) => params.value,
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'Delivery Window',
                    headerTooltip: 'Delivery Window',
                    field: 'deliveryWindow',
                    minWidth: constants.columnWidth.W_120,
                    cellClass: 'gridCell-leftAlign',
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'Customer Name',
                    headerTooltip: 'Customer Name',
                    field: 'customerName',
                    minWidth: constants.columnWidth.W_180,
                    cellClass: 'gridCell-leftAlign',
                    tooltipValueGetter: (params) => params.value,
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'Customer Code',
                    headerTooltip: 'Customer Code',
                    field: 'displayCustomerCode',
                    minWidth: constants.columnWidth.W_120,
                    cellClass: 'gridCell-leftAlign',
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'Customer Address',
                    headerTooltip: 'Customer Address',
                    field: 'customerAddress',
                    minWidth: constants.columnWidth.W_160,
                    cellClass: 'gridCell-leftAlign',
                    tooltipValueGetter: (params) => params.value,
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'Customer City',
                    headerTooltip: 'Customer City',
                    field: 'customerCity',
                    minWidth: constants.columnWidth.W_140,
                    cellClass: 'gridCell-leftAlign',
                    tooltipValueGetter: (params) => params.value,
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'Customer State',
                    headerTooltip: 'Customer State',
                    field: 'customerState',
                    minWidth: constants.columnWidth.W_120,
                    cellClass: 'gridCell-leftAlign',
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'Customer Zip',
                    headerTooltip: 'Customer Zip',
                    field: 'customerZip',
                    minWidth: constants.columnWidth.W_120,
                    cellClass: 'gridCell-leftAlign',
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'Weight',
                    headerTooltip: 'Weight',
                    field: 'weight',
                    minWidth: constants.columnWidth.W_80,
                    cellClass: 'gridCell-leftAlign',
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'PSE',
                    headerTooltip: 'PSE',
                    field: 'pse',
                    minWidth: constants.columnWidth.W_70,
                    cellClass: 'gridCell-leftAlign',
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'HU',
                    headerTooltip: 'HU',
                    field: 'hu',
                    minWidth: constants.columnWidth.W_70,
                    cellClass: 'gridCell-leftAlign',
                    filter: MultiSelectFilter,
                },
                {
                    headerName: 'Spl Needs',
                    headerTooltip: 'Special Needs',
                    minWidth: constants.columnWidth.SPL_NEED,
                    cellClass: 'gridCell-leftAlign',
                    valueGetter: getSpecialNeeds,
                    cellRenderer: SpecialNeedsRenderer,
                    filter: StaticFilter,
                    filterParams: {
                        getStaticOptions: GridConfig.getSpecialNeedsOption,
                    },
                },
            ],
        };
    }
}
